/*
 * Peel Bundle Builder — case + glass + accessories, configured and added
 * to the cart in one go. Loaded on the "bundle" and "bundles" page
 * templates: the builder page lists several bundles, and each bundle's
 * own page on page.bundles shows one through the same card.
 *
 * Each bundle card is an independent controller over the same shape of
 * state: { caseType, model, color, glass }. Picking a case type or a phone
 * resolves the real product behind it; picking a phone also re-cuts the
 * glass, which is why the shopper never chooses a glass size — only its
 * finish.
 *
 * Products are resolved through Shopify's predictive search, read back via
 * the peel-bundle-lookup section. Peel cuts a separate product for every
 * case family × phone, far past the 50 Liquid can read out of a
 * collection, so the catalogue cannot be embedded in the page.
 *
 * What it costs is a different question from what is in it: the bundle is
 * sold at a stated price per tier of case family, so picking Stealth moves
 * the price as well as the case. Blocks without a price table fall back to
 * pricing off the live total, which is what every bundle did before the
 * tiers existed.
 */
(function () {
  var root = document.querySelector('[data-peel-bundle]');
  if (!root) return;

  var scope = root.closest('.shopify-section') || document;
  var configEl = scope.querySelector('[data-peel-bundle-config]');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  var labels = config.labels || {};
  var brands = config.brands || [];

  /*
   * A page that types no phones of its own (each bundle's page on
   * page.bundles) lists the ones the store's products declare, from the
   * catalogue peel-device-catalog renders and peel-device-map.js orders
   * newest first. The labels are how the phone reads in a product title
   * ("Galaxy S26 Ultra", "Pixel 10 Pro"), which is what the case lookup
   * matches on.
   */
  if (!brands.length && window.peelDeviceMap && window.peelDeviceMap.catalog) {
    brands = window.peelDeviceMap.catalog().map(function (brand) {
      return {
        name: brand.name,
        models: brand.models.map(function (model) {
          return model.model;
        })
      };
    });
  }
  var glassTypes = config.glassTypes || [];

  /* ---- Helpers ---- */

  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function money(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fill(template, token, value) {
    return String(template || '').split(token).join(value);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  /*
   * Does this product title name this exact phone?
   *
   * Titles put the model in three different places — "Active iPhone 17 Pro
   * Case", "iPhone 15 Glass Screen Protector", "Glass Screen Protector for
   * iPhone 16e" — so the model is matched anywhere in the title rather
   * than at the end. The catch is that "iPhone 16 Pro" contains
   * "iPhone 16", so a bare match would sell somebody the wrong size: if the
   * word straight after the match is a size qualifier, this title belongs
   * to a bigger phone and does not match.
   */
  var QUALIFIERS = ['pro', 'max', 'plus', 'mini', 'ultra', 'xl', 'air', 'fe', 'note'];

  function normalize(value) {
    return ' ' + String(value || '').toLowerCase().replace(/\s+/g, ' ').trim() + ' ';
  }

  function modelMatches(title, model) {
    var haystack = normalize(title);
    var needle = normalize(model);
    var at = haystack.indexOf(needle);
    while (at !== -1) {
      var rest = haystack.slice(at + needle.length).trim();
      var next = rest ? rest.split(' ')[0] : '';
      if (QUALIFIERS.indexOf(next) === -1) return true;
      at = haystack.indexOf(needle, at + 1);
    }
    return false;
  }

  /* Whole words only, so "Active" doesn't match "Interactive". */
  function titleContains(title, term) {
    if (!term) return true;
    return normalize(title).indexOf(normalize(term)) !== -1;
  }

  /* ---- Product lookup ----
     One in-flight request per query, and answers kept for the life of the
     page: flipping between two case types shouldn't re-ask for either. */

  var lookupCache = {};

  function lookup(query) {
    if (lookupCache[query]) return lookupCache[query];

    var url =
      routes +
      'search/suggest?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=10&section_id=peel-bundle-lookup';

    var request = fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        var node = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-peel-bundle-lookup]');
        return node ? JSON.parse(node.textContent) : [];
      })
      .catch(function () {
        /* A failed lookup shouldn't be cached as "no such product". */
        delete lookupCache[query];
        return [];
      });

    lookupCache[query] = request;
    return request;
  }

  /*
   * The product type alone isn't enough to identify a case. Peel files the
   * odd combination product under a case type too — "iPhone 17 Case +
   * Screen Protection Bundle" is a Magnetic Case — and it names the right
   * phone, so it would be picked ahead of the case the shopper asked for.
   * Requiring the family in the title as well ("Magnetic") rules those out,
   * because a real case always carries its family name.
   */
  function pick(products, productType, model, requiredTerm) {
    var wanted = String(productType || '').toLowerCase();
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (wanted && String(product.type || '').toLowerCase() !== wanted) continue;
      if (!titleContains(product.title, requiredTerm)) continue;
      if (!modelMatches(product.title, model)) continue;
      return product;
    }
    return null;
  }

  /* ---- One bundle card ---- */

  function BundleCard(card) {
    var id = card.getAttribute('data-bundle-id');
    var spec = (config.bundles || {})[id];
    if (!spec) return;

    var caseTypes = spec.caseTypes || [];
    var extras = spec.extras || [];
    var showGlass = spec.showGlass && glassTypes.length > 0;

    /* The price table, one entry per tier, with the case families it
       covers already split off the merchant's comma-separated line. */
    var priceTiers = (spec.priceTiers || []).map(function (tier) {
      return {
        cases: String(tier.cases || '')
          .split(',')
          .map(function (name) {
            return name.trim().toLowerCase();
          })
          .filter(Boolean),
        compare: tier.compare,
        price: tier.price,
        discountCode: tier.discountCode
      };
    });

    /* Which tier this case family is sold in, or null when the merchant
       hasn't priced it — in which case the card prices off the live
       total instead of showing a figure nobody set. */
    function tierFor(choice) {
      if (!choice) return null;
      var label = String(choice.label || '').trim().toLowerCase();
      for (var t = 0; t < priceTiers.length; t++) {
        if (priceTiers[t].cases.indexOf(label) !== -1) return priceTiers[t];
      }
      return null;
    }

    var els = {
      hero: card.querySelector('[data-bundle-hero]'),
      heroEmpty: card.querySelector('[data-bundle-hero-empty]'),
      badge: card.querySelector('[data-bundle-badge]'),
      thumbs: card.querySelector('[data-bundle-thumbs]'),
      contents: card.querySelector('[data-bundle-contents]'),
      caseTypes: card.querySelector('[data-bundle-case-types]'),
      caseTypeField: card.querySelector('[data-bundle-field="caseType"]'),
      model: card.querySelector('[data-bundle-model]'),
      colorField: card.querySelector('[data-bundle-field="color"]'),
      colors: card.querySelector('[data-bundle-colors]'),
      colorName: card.querySelector('[data-bundle-color-name]'),
      glassField: card.querySelector('[data-bundle-field="glass"]'),
      glassTypes: card.querySelector('[data-bundle-glass-types]'),
      glassNote: card.querySelector('[data-bundle-glass-note]'),
      status: card.querySelector('[data-bundle-status]'),
      price: card.querySelector('[data-bundle-price]'),
      compare: card.querySelector('[data-bundle-compare]'),
      savings: card.querySelector('[data-bundle-savings]'),
      add: card.querySelector('[data-bundle-add]')
    };

    var hasOwnImage = !!(els.hero && els.hero.getAttribute('src'));

    var state = {
      caseType: caseTypes.length ? caseTypes[0] : null,
      model: '',
      color: null,
      glass: showGlass ? glassTypes[0] : null,
      caseProduct: undefined,
      /* case type label -> whether it is cut for the chosen phone; a label
         still missing is a lookup that hasn't landed. */
      caseFits: {},
      /* finish key -> product, or null once we know it isn't cut for this
         phone. A key still missing is a lookup that hasn't landed. */
      glassProducts: {},
      busy: false
    };

    /* ---- Build the controls that don't change ---- */

    if (caseTypes.length > 1) {
      caseTypes.forEach(function (choice) {
        var pill = el('button', 'peel-bundle-pill', choice.label);
        pill.type = 'button';
        pill.setAttribute('aria-pressed', 'false');
        pill.addEventListener('click', function () {
          if (pill.disabled || state.caseType === choice) return;
          state.caseType = choice;
          state.caseProduct = undefined;
          render();
          resolveCase();
        });
        choice.el = pill;
        els.caseTypes.appendChild(pill);
      });
    } else if (els.caseTypeField) {
      /* One choice is not a choice — name it instead of pilling it. */
      els.caseTypeField.classList.add('peel-bundle-field--stated');
      els.caseTypes.appendChild(el('div', 'peel-bundle-field-value', caseTypes.length ? caseTypes[0].label : ''));
    }

    brands.forEach(function (brand) {
      var models = brand.models || [];
      if (!models.length) return;
      var group = document.createElement('optgroup');
      group.label = brand.name;
      models.forEach(function (model) {
        var option = el('option', null, model);
        option.value = model;
        group.appendChild(option);
      });
      els.model.appendChild(group);
    });

    /* A phone chosen anywhere else on the site — the homepage picker, a
       collection hero — is the one the shopper already told us about. */
    var remembered = window.peelDeviceSelection && window.peelDeviceSelection.get();
    if (remembered && remembered.model) {
      var options = els.model.querySelectorAll('option');
      for (var i = 0; i < options.length; i++) {
        if (modelMatches(options[i].value, remembered.model) || options[i].value === remembered.model) {
          els.model.value = options[i].value;
          break;
        }
      }
    }
    state.model = els.model.value || (els.model.options.length ? els.model.options[0].value : '');
    els.model.value = state.model;

    els.model.addEventListener('change', function () {
      state.model = els.model.value;
      state.caseProduct = undefined;
      state.caseFits = {};
      state.glassProducts = {};
      render();
      resolveCase();
      resolveGlass();
      checkCaseTypes();
    });

    if (showGlass && els.glassTypes) {
      glassTypes.forEach(function (choice) {
        var pill = el('button', 'peel-bundle-pill', choice.label);
        pill.type = 'button';
        pill.setAttribute('aria-pressed', 'false');
        pill.addEventListener('click', function () {
          if (pill.disabled || state.glass === choice) return;
          state.glass = choice;
          render();
        });
        choice.els = choice.els || {};
        choice.els[id] = pill;
        els.glassTypes.appendChild(pill);
      });
    }

    /* ---- Resolving the configured products ---- */

    /*
     * Which case types are cut for the chosen phone, asked of every type at
     * once so the ones that aren't can be greyed out before the shopper
     * reaches for them. The lookups are the ones resolveCase makes and are
     * cached, so the type picked costs nothing extra. If the type picked
     * turns out not to fit, the card moves to the first one that does
     * rather than sitting on a dead end.
     */
    function checkCaseTypes() {
      if (caseTypes.length < 2 || !state.model) return;
      var model = state.model;
      caseTypes.forEach(function (choice) {
        lookup(choice.label + ' ' + model + ' Case').then(function (products) {
          if (state.model !== model) return;
          state.caseFits[choice.label] = !!pick(products, choice.productType, model, choice.label);

          if (state.caseFits[state.caseType.label] === false) {
            for (var c = 0; c < caseTypes.length; c++) {
              if (state.caseFits[caseTypes[c].label] === true) {
                state.caseType = caseTypes[c];
                state.caseProduct = undefined;
                resolveCase();
                break;
              }
            }
          }
          render();
        });
      });
    }

    function resolveCase() {
      if (!state.caseType || !state.model) return;
      var choice = state.caseType;
      var model = state.model;
      var token = choice.productType + '|' + model;
      resolveCase.pending = token;

      lookup(choice.label + ' ' + model + ' Case').then(function (products) {
        if (resolveCase.pending !== token) return;
        state.caseProduct = pick(products, choice.productType, model, choice.label);
        chooseColor();
        render();
      });
    }

    /*
     * Every finish is looked up, not just the selected one. Peel cuts
     * privacy glass for far fewer phones than standard, so the card has to
     * know which finishes exist for this phone before the shopper picks
     * one — otherwise choosing Privacy on a phone we don't cut it for is a
     * dead end they can only discover by hitting it.
     */
    function resolveGlass() {
      if (!showGlass || !state.model) return;
      var model = state.model;
      resolveGlass.pending = model;

      glassTypes.forEach(function (choice) {
        lookup(model + ' ' + choice.productType).then(function (products) {
          if (resolveGlass.pending !== model) return;
          state.glassProducts[choice.key] = pick(products, choice.productType, model, choice.productType);
          fallBackToAvailableGlass();
          render();
        });
      });
    }

    /* Their finish isn't cut for this phone — move them to one that is,
       rather than leaving the bundle unbuyable with no way out. */
    function fallBackToAvailableGlass() {
      if (!state.glass || state.glassProducts[state.glass.key] !== null) return;
      for (var i = 0; i < glassTypes.length; i++) {
        if (state.glassProducts[glassTypes[i].key]) {
          state.glass = glassTypes[i];
          return;
        }
      }
    }

    /* Keep the colourway across a phone change when the new case still
       comes in it; otherwise fall to the first one in stock. */
    function chooseColor() {
      var variants = sellableVariants(state.caseProduct);
      if (!variants.length) {
        state.color = null;
        return;
      }
      if (state.color) {
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].color === state.color.color) {
            state.color = variants[i];
            return;
          }
        }
      }
      state.color = variants[0];
    }

    function sellableVariants(product) {
      if (!product) return [];
      return (product.variants || []).filter(function (variant) {
        return variant.available;
      });
    }

    function caseVariant() {
      if (!state.caseProduct) return null;
      if (state.color) return state.color;
      return sellableVariants(state.caseProduct)[0] || null;
    }

    function glassProduct() {
      if (!showGlass || !state.glass) return null;
      return state.glassProducts[state.glass.key] || null;
    }

    function glassVariant() {
      var product = glassProduct();
      if (!product) return null;
      return sellableVariants(product)[0] || null;
    }

    /* ---- Rendering ---- */

    function renderCaseTypes() {
      caseTypes.forEach(function (choice) {
        if (!choice.el) return;
        var on = choice === state.caseType;
        /* Taken out of the row once we know it isn't cut for this phone;
           a type still being looked up stays offered. */
        var missing = state.caseFits[choice.label] === false;
        choice.el.setAttribute('aria-pressed', on ? 'true' : 'false');
        choice.el.classList.toggle('is-selected', on && !missing);
        choice.el.disabled = missing;
        choice.el.hidden = missing;
      });
    }

    function renderGlassTypes() {
      if (!showGlass) return;
      glassTypes.forEach(function (choice) {
        var pill = choice.els && choice.els[id];
        if (!pill) return;
        var on = choice === state.glass;
        var missing = state.glassProducts[choice.key] === null;
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
        pill.classList.toggle('is-selected', on && !missing);
        pill.disabled = missing;
        pill.title = missing ? fill(labels.unavailable, '[model]', state.model) : '';
      });

      if (els.glassNote) {
        var warn = glassProduct() === null && state.glassProducts[state.glass.key] === null;
        els.glassNote.textContent = warn
          ? fill(labels.unavailable, '[model]', state.model)
          : fill(labels.cutTo, '[model]', state.model);
        els.glassNote.classList.toggle('is-warning', warn);
      }
    }

    function renderColors() {
      var variants = sellableVariants(state.caseProduct);
      var named = variants.filter(function (variant) {
        return !!variant.color;
      });

      if (!els.colorField) return;
      els.colorField.hidden = named.length < 2;
      if (named.length < 2) return;

      els.colors.innerHTML = '';
      named.forEach(function (variant) {
        var swatch = el('button', 'peel-bundle-swatch');
        swatch.type = 'button';
        swatch.style.setProperty('--peel-swatch', variant.swatch || '#B0AA9E');
        swatch.setAttribute('aria-label', variant.color);
        var on = state.color && state.color.id === variant.id;
        swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
        swatch.classList.toggle('is-selected', on);
        swatch.addEventListener('click', function () {
          state.color = variant;
          render();
        });
        els.colors.appendChild(swatch);
      });

      if (els.colorName) els.colorName.textContent = state.color ? state.color.color || '' : '';
    }

    function renderContents() {
      if (!els.contents) return;
      els.contents.innerHTML = '';

      var lines = [];
      var chosenCase = caseVariant();
      lines.push({
        item: (state.caseType ? state.caseType.label + ' Case' : 'Case'),
        detail: state.caseProduct
          ? fill(labels.cutTo, '[model]', state.model) +
            (chosenCase && chosenCase.color ? ' · ' + chosenCase.color : '')
          : fill(labels.noFit, '[model]', state.model)
      });

      if (showGlass) {
        lines.push({
          item: spec.glassNoteLabel || 'Peel Glass',
          detail: glassProduct()
            ? (state.glass ? state.glass.label + ' · ' : '') + fill(labels.cutTo, '[model]', state.model)
            : fill(labels.unavailable, '[model]', state.model)
        });
      }

      extras.forEach(function (extra) {
        var detail = labels.soldOut || 'Sold out';
        if (extra.available) {
          /* The colourway is fixed by the merchant, so it reads as part of
             the line rather than as a choice the shopper still has. */
          detail = extra.variantTitle ? extra.variantTitle + ' · ' + money(extra.price) : money(extra.price);
        }
        lines.push({ item: extra.title, detail: detail });
      });

      lines.forEach(function (line) {
        var row = el('li', 'peel-bundle-content');
        var text = el('span', 'peel-bundle-content-text');
        text.appendChild(el('span', 'peel-bundle-content-item', line.item));
        text.appendChild(el('span', 'peel-bundle-content-detail', line.detail));
        row.appendChild(text);
        els.contents.appendChild(row);
      });
    }

    function renderMedia() {
      var chosenCase = caseVariant();
      var hero = (chosenCase && chosenCase.image) || (state.caseProduct && state.caseProduct.image);

      if (els.hero && !hasOwnImage) {
        if (hero) {
          els.hero.src = hero;
          els.hero.alt = state.caseProduct ? state.caseProduct.title : spec.name;
          els.hero.hidden = false;
          if (els.heroEmpty) els.heroEmpty.hidden = true;
        } else {
          els.hero.hidden = true;
          els.hero.removeAttribute('src');
          if (els.heroEmpty) els.heroEmpty.hidden = false;
        }
      }

      if (!els.thumbs) return;
      els.thumbs.innerHTML = '';

      var shots = [];
      if (hero) shots.push({ src: hero, label: state.caseType ? state.caseType.label : 'Case' });
      var glass = glassProduct();
      if (glass && glass.image) {
        shots.push({ src: glass.image, label: state.glass ? state.glass.label : 'Glass' });
      }
      extras.forEach(function (extra) {
        if (extra.image) shots.push({ src: extra.image, label: extra.title });
      });

      shots.slice(0, 3).forEach(function (shot) {
        var cell = el('div', 'peel-bundle-thumb');
        var frame = el('div', 'peel-bundle-thumb-frame');
        var img = el('img', 'peel-bundle-thumb-img');
        img.src = shot.src;
        img.alt = shot.label;
        img.loading = 'lazy';
        frame.appendChild(img);
        cell.appendChild(frame);
        cell.appendChild(el('div', 'peel-bundle-thumb-label', shot.label));
        els.thumbs.appendChild(cell);
      });
    }

    /* The bundle is only sellable when every piece of it resolved and is
       in stock — a bundle that drops a piece silently is worse than one
       that says it can't be built. */
    function lineItems() {
      var items = [];
      var chosenCase = caseVariant();
      if (!chosenCase) return null;
      items.push({ id: chosenCase.id, price: chosenCase.price });

      if (showGlass) {
        var chosenGlass = glassVariant();
        if (!chosenGlass) return null;
        items.push({ id: chosenGlass.id, price: chosenGlass.price });
      }

      for (var i = 0; i < extras.length; i++) {
        if (!extras[i].available) return null;
        items.push({ id: extras[i].id, price: extras[i].price });
      }
      return items;
    }

    function renderPricing() {
      var items = lineItems();
      var pending =
        state.caseProduct === undefined ||
        (showGlass && !!state.glass && state.glassProducts[state.glass.key] === undefined);

      if (!items) {
        els.price.textContent = '—';
        els.compare.hidden = true;
        els.savings.hidden = true;
        els.add.disabled = true;

        if (els.status) {
          if (pending) {
            els.status.hidden = true;
          } else {
            els.status.hidden = false;
            els.status.textContent = fill(labels.noFit, '[model]', state.model);
            els.status.classList.remove('is-error');
          }
        }
        return;
      }

      if (els.status && !els.status.classList.contains('is-error')) els.status.hidden = true;

      /* Tier price when the merchant stated one, live total less the
         percentage and any fixed amount when they didn't. */
      var tier = tierFor(state.caseType);
      var total;
      var compareAt;

      if (tier) {
        total = tier.price;
        compareAt = tier.compare;
      } else {
        compareAt = items.reduce(function (sum, item) {
          return sum + item.price;
        }, 0);
        var percent = Number(spec.discountPercent) || 0;
        var amountOff = Number(spec.discountAmount) || 0;
        total = Math.max(0, Math.round(compareAt * (1 - percent / 100)) - amountOff);
      }
      var saving = compareAt - total;

      els.price.textContent = money(total);
      if (saving > 0) {
        var savingText = fill(
          fill(labels.savings, '[amount]', money(saving)),
          '[percent]',
          Math.round((saving / compareAt) * 100) + '%'
        );
        els.compare.hidden = false;
        els.compare.textContent = money(compareAt);
        els.compare.title = labels.separately || 'Bought separately';
        els.savings.hidden = false;
        els.savings.textContent = savingText;
        if (els.badge) {
          els.badge.hidden = false;
          els.badge.textContent = savingText;
        }
      } else {
        els.compare.hidden = true;
        els.savings.hidden = true;
        if (els.badge) els.badge.hidden = true;
      }

      els.add.disabled = state.busy;
    }

    function render() {
      renderCaseTypes();
      renderColors();
      renderGlassTypes();
      renderContents();
      renderMedia();
      renderPricing();
    }

    /* ---- Cart ---- */

    function addToCart() {
      var items = lineItems();
      if (!items || state.busy) return;

      state.busy = true;
      els.add.disabled = true;
      els.add.textContent = labels.adding || 'Adding…';
      if (els.status) {
        els.status.hidden = true;
        els.status.classList.remove('is-error');
      }

      /* Tiers sell at different prices, so the code that honours this one
         is the tier's if it names one, and the block's otherwise. */
      var tier = tierFor(state.caseType);
      var discountCode = (tier && tier.discountCode) || spec.discountCode;

      /*
       * Named on every line so an automatic discount or a Shopify Function
       * can recognise the bundle in the cart, and stamped with an id so the
       * cart can tell one bundle from another.
       *
       * The id has to be unique per add rather than per bundle: two orders
       * of the same bundle for two different phones are two bundles, and
       * without it they would read as one muddled group holding six pieces.
       * It also stops Shopify merging a second identical bundle into the
       * first one's lines, which is what makes buying two of the same
       * bundle show up as two cards rather than a quantity of 2 on each
       * piece.
       *
       * Both are underscore-prefixed, so neither is shown to the customer
       * at checkout or on the order.
       */
      var bundleId = spec.name + ':' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

      var payload = items.map(function (item) {
        return {
          id: item.id,
          quantity: 1,
          properties: { _peel_bundle: spec.name, _peel_bundle_id: bundleId }
        };
      });

      fetch(routes + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload, sections: ['cart-drawer'] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then(function (cart) {
          /* A discount code has to be applied by the cart, not by us —
             this hands the shopper to Shopify, which applies it and comes
             straight back to the cart. */
          if (discountCode) {
            window.location.href =
              routes + 'discount/' + encodeURIComponent(discountCode) + '?redirect=' + encodeURIComponent(routes + 'cart');
            return;
          }

          var drawerSection = document.getElementById('shopify-section-cart-drawer');
          if (drawerSection && cart.sections && cart.sections['cart-drawer']) {
            drawerSection.outerHTML = cart.sections['cart-drawer'];
          }
          var drawer = document.getElementById('cartDrawer');
          if (drawer && typeof drawer.showModal === 'function') drawer.showModal();
          /* cart/add.js sends back the added lines, not the cart, so
             item_count is undefined here; _updateCartItemCount then reads the
             count off the drawer this response re-rendered. */
          if (typeof window._updateCartItemCount === 'function') {
            window._updateCartItemCount(cart.item_count);
          }

          state.busy = false;
          els.add.textContent = labels.added || 'Added to cart';
          window.setTimeout(function () {
            els.add.textContent = labels.addLabel || 'Add bundle to cart';
            render();
          }, 1600);
          render();
        })
        .catch(function () {
          state.busy = false;
          els.add.textContent = labels.addLabel || 'Add bundle to cart';
          if (els.status) {
            els.status.hidden = false;
            els.status.classList.add('is-error');
            els.status.textContent = labels.error || "That didn't go through. Try again?";
          }
          render();
        });
    }

    els.add.addEventListener('click', addToCart);

    render();
    resolveCase();
    resolveGlass();
    checkCaseTypes();
  }

  root.querySelectorAll('[data-peel-bundle-card]').forEach(BundleCard);
})();

